import { Link } from 'react-router-dom';
import { useMyTime, sourceLabel } from '../api/time.ts';
import { clientWords } from '../time/view.ts';
import { describeMinutes } from '../duration.ts';
import { formatDate } from '../dates.ts';
import { useKeptState } from '../components/OpenTabs.tsx';
import { agencyToday } from '../components/tasks/FollowUp.tsx';
import { plain } from '../components/tasks/Fields.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { addDays, mondayOf, weekdayShort } from '../time/days.ts';

/**
 * My time (money plan phase 1, 161; Arthur's Q1): ALL of the reader's own time for a week, Monday to Sunday, whoever
 * the client is assigned to now. A client the reader no longer sees shows its minutes and "a client you no longer
 * see", never its name (Q8). Every kind of time is counted. The week is kept while the tab is open, in memory only.
 */

export function MyTime() {
  const [week, setWeek] = useKeptState<string>('myTime', '');
  const q = useMyTime(week);
  const shown = q.data?.week ?? (week || mondayOf(agencyToday()));
  const go = (n: number) => setWeek(addDays(shown, 7 * n));

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">My time</h1>
          <p className="mt-1 max-w-[60rem] text-sm text-neutral-700">
            Everything you logged: tasks and actions, welfare visits, site visits and communications, on any client,
            including clients since reassigned. Minutes are exact.
          </p>
        </header>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button" className={plain} onClick={() => go(-1)}>Previous week</button>
          <button type="button" className={plain} onClick={() => setWeek('')}>This week</button>
          <button type="button" className={plain} onClick={() => go(1)}>Next week</button>
          <span className="text-neutral-900">Week of {formatDate(shown)}</span>
        </div>
        {q.error && !q.data ? <LoadFailure what="Your time" error={q.error} onRetry={() => void q.refetch()} />
          : !q.data ? <p className="py-3 text-neutral-700">Loading…</p>
          : (
            <>
              <table className="mt-4 w-full max-w-[48rem] text-sm">
                <thead>
                  <tr className="border-b border-neutral-300 text-left text-neutral-700">
                    {q.data.days.map((d) => <th key={d.on_date} className="py-1.5 font-semibold">{weekdayShort(d.on_date)} {formatDate(d.on_date)}</th>)}
                    <th className="py-1.5 text-right font-semibold">Week</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="text-neutral-900">
                    {q.data.days.map((d) => <td key={d.on_date} className="py-1.5 tabular-nums">{describeMinutes(d.minutes)}</td>)}
                    <td className="py-1.5 text-right font-semibold tabular-nums">{describeMinutes(q.data.total)}</td>
                  </tr>
                </tbody>
              </table>
              {q.data.total === 0 ? <p className="py-3 text-neutral-700">No time logged this week.</p> : (
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <section aria-labelledby="mine-dept">
                    <h2 id="mine-dept" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">By department</h2>
                    <ul className="mt-1">{q.data.by_department.map((d) => (
                      <li key={d.department} className="flex justify-between border-b border-neutral-200 py-1.5 text-sm">
                        <span>{d.department}</span><span className="tabular-nums">{describeMinutes(d.minutes)}</span></li>))}</ul>
                  </section>
                  <section aria-labelledby="mine-source">
                    <h2 id="mine-source" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">By kind</h2>
                    <ul className="mt-1">{q.data.by_source.map((s) => (
                      <li key={s.source} className="flex justify-between border-b border-neutral-200 py-1.5 text-sm">
                        <span>{sourceLabel(s.source)}</span><span className="tabular-nums">{describeMinutes(s.minutes)}</span></li>))}</ul>
                  </section>
                  <section aria-labelledby="mine-entries">
                    <h2 id="mine-entries" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">Entries</h2>
                    <ul className="mt-1">{q.data.entries.map((e, i) => (
                      <li key={i} className="flex flex-wrap justify-between gap-2 border-b border-neutral-200 py-1.5 text-sm">
                        <span>
                          {formatDate(e.on_date)} · {sourceLabel(e.source, e.is_action)} ·{' '}
                          {e.client && e.client !== 'hidden'
                            ? <Link to={`/clients/${e.client.id}`} className="text-brand-text underline">{clientWords(e.client)}</Link>
                            : clientWords(e.client)}
                        </span>
                        <span className="tabular-nums">{describeMinutes(e.minutes)}</span>
                      </li>))}</ul>
                  </section>
                </div>
              )}
            </>
          )}
      </div>
    </div>
  );
}
